/**
 * Agent Edit Page - 6-Step Wizard (Edit Mode)
 * CreateAgent'tan component'leri kullanir, mevcut agent'i yukler
 *
 * Steps:
 * 1. Basic Info (name, bio, avatar)
 * 2. Persona Selection (Expert, Baby, Mentor, Master, Custom)
 * 3. Personality Traits (sliders: creativity, formality, humor, detail, confidence)
 * 4. Expertise Areas (multi-select tags with proficiency)
 * 5. Contribution Settings (frequency, patterns, confidence threshold)
 * 6. Preview Mode (test how agent will respond)
 */

import { Brain, Check, ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AgentPreview from '../components/agent/AgentPreview';
import ContributionSettings from '../components/agent/ContributionSettings';
import ExpertiseSelector from '../components/agent/ExpertiseSelector';
import PersonaSelector from '../components/agent/PersonaSelector';
import PersonalityTraitsEditor from '../components/agent/PersonalityTraitsEditor';
import MainLayout from '../components/layout/MainLayout';
import { type AgentPersona } from '../data/agentPersonas';
import { useToast } from '../hooks/useToast';
import api from '../services/api';

interface AgentFormData {
  // Step 1: Basic Info
  name: string;
  bio: string;
  avatar_url: string;

  // Step 2: Persona
  persona_id: string;
  agent_type: 'expert' | 'baby' | 'mentor' | 'master';

  // Step 3: Personality Traits
  personality_creativity: number;
  personality_formality: number;
  personality_humor: number;
  personality_detail: number;
  personality_confidence: number;

  // Step 4: Expertise Areas
  expertise_areas: Array<{
    name: string;
    category: string;
    proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  }>;

  // Step 5: Contribution Settings
  contribution_frequency: 'very_active' | 'active' | 'moderate' | 'occasional' | 'rare';
  contribution_pattern: 'helpful' | 'contrarian' | 'balanced' | 'supportive';
  confidence_threshold: number;
}

export default function AgentEdit(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    bio: '',
    avatar_url: '',
    persona_id: '',
    agent_type: 'expert',
    personality_creativity: 50,
    personality_formality: 50,
    personality_humor: 50,
    personality_detail: 50,
    personality_confidence: 50,
    expertise_areas: [],
    contribution_frequency: 'moderate',
    contribution_pattern: 'balanced',
    confidence_threshold: 0.6
  });

  // Mevcut agent'i yukle
  useEffect(() => {
    const loadAgent = async () => {
      if (!id) {
        toast.error('Agent ID bulunamadi');
        navigate('/agents');
        return;
      }

      try {
        setInitialLoading(true);
        const response = await api.get(`/api/agents/${id}`);
        const agent = response.data;

        // Form data'yi doldur
        setFormData({
          name: agent.name ?? '',
          bio: agent.bio ?? '',
          avatar_url: agent.avatar_url ?? '',
          persona_id: agent.persona_id ?? '',
          agent_type: agent.agent_type ?? 'expert',
          personality_creativity: agent.personality_creativity ?? 50,
          personality_formality: agent.personality_formality ?? 50,
          personality_humor: agent.personality_humor ?? 50,
          personality_detail: agent.personality_detail ?? 50,
          personality_confidence: agent.personality_confidence ?? 50,
          expertise_areas: agent.expertise_areas ?? [],
          contribution_frequency: agent.contribution_frequency ?? 'moderate',
          contribution_pattern: agent.contribution_pattern ?? 'balanced',
          confidence_threshold: agent.confidence_threshold ?? 0.6
        });
      } catch (err) {
        const error = err as { response?: { data?: { detail?: string } } };
        toast.error(error.response?.data?.detail ?? 'Agent yuklenemedi');
        navigate('/agents');
      } finally {
        setInitialLoading(false);
      }
    };

    void loadAgent();
  }, [id, navigate, toast]);

  // Persona secildiginde personality traits'i uygula
  const handlePersonaSelect = (persona: AgentPersona) => {
    setFormData(prev => ({
      ...prev,
      persona_id: persona.id,
      agent_type: persona.id as 'expert' | 'baby' | 'mentor' | 'master',
      personality_creativity: persona.personalityTraits.creativity,
      personality_formality: persona.personalityTraits.formality,
      personality_humor: persona.personalityTraits.humor,
      personality_detail: persona.personalityTraits.detail,
      personality_confidence: persona.personalityTraits.confidence,
      contribution_frequency: persona.contributionFrequency,
      contribution_pattern: persona.contributionPattern,
      confidence_threshold: persona.confidenceThreshold
    }));
  };

  // Personality trait degisikligi
  const handleTraitChange = (trait: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [`personality_${trait}`]: value
    }));
  };

  // Expertise ekleme
  const addExpertise = (name: string, category: string, proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert') => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: [...prev.expertise_areas, { name, category, proficiency }]
    }));
  };

  // Expertise cikarma
  const removeExpertise = (index: number) => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.filter((_, i) => i !== index)
    }));
  };

  // Validation
  const validateStep1 = (): boolean => {
    if (!formData.name || formData.name.length < 3) {
      toast.error('Agent adi en az 3 karakter olmali');
      return false;
    }
    if (!formData.bio || formData.bio.length < 20) {
      toast.error('Bio en az 20 karakter olmali');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.persona_id) {
      toast.error('Bir persona secmelisiniz');
      return false;
    }
    return true;
  };

  const validateStep4 = (): boolean => {
    if (formData.expertise_areas.length === 0) {
      toast.error('En az bir expertise alani secmelisiniz');
      return false;
    }
    return true;
  };

  // Navigation
  const handleNext = () => {
    if (step === 1 && !validateStep1()) {
      return;
    }
    if (step === 2 && !validateStep2()) {
      return;
    }
    if (step === 4 && !validateStep4()) {
      return;
    }

    if (step < 6) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Submit (Update)
  const handleSubmit = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      await api.put(`/api/agents/${id}`, formData);
      toast.success('Agent basariyla guncellendi!');
      navigate(`/agents/${id}`);
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(error.response?.data?.detail ?? 'Agent guncellenemedi');
      setLoading(false);
    }
  };

  // Initial loading state
  if (initialLoading) {
    return (
      <MainLayout>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 60px)',
          background: '#f8fafc'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Loader2 size={48} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '16px', color: '#64748b' }}>Agent yukleniyor...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{
        background: '#f8fafc',
        minHeight: 'calc(100vh - 60px)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '32px', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              marginBottom: '16px'
            }}>
              <Brain size={32} color="#fff" />
            </div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '8px'
            }}>Agent Duzenle</h1>
            <p style={{
              fontSize: '16px',
              color: '#64748b'
            }}>Agent&apos;inizin ayarlarini guncelleyin</p>
          </div>

          {/* Progress Stepper */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '40px',
            position: 'relative'
          }}>
            {/* Progress Line */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '0',
              right: '0',
              height: '2px',
              background: '#e2e8f0',
              zIndex: 0
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                width: `${((step - 1) / 5) * 100}%`,
                transition: 'width 0.3s ease'
              }} />
            </div>

            {/* Steps */}
            {[
              { num: 1, label: 'Temel Bilgiler' },
              { num: 2, label: 'Persona' },
              { num: 3, label: 'Kisilik' },
              { num: 4, label: 'Uzmanlik' },
              { num: 5, label: 'Ayarlar' },
              { num: 6, label: 'Onizleme' }
            ].map(s => (
              <div key={s.num} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                zIndex: 1,
                flex: 1
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: step >= s.num ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#fff',
                  color: step >= s.num ? '#fff' : '#94a3b8',
                  border: step >= s.num ? 'none' : '2px solid #e2e8f0',
                  transition: 'all 0.3s ease'
                }}>
                  {step > s.num ? <Check size={20} /> : s.num}
                </div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: step >= s.num ? '#1a202c' : '#94a3b8',
                  textAlign: 'center'
                }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Content Card */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            padding: '40px',
            minHeight: '500px'
          }}>
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
                  Temel Bilgiler
                </h2>
                <p style={{ color: '#64748b', marginBottom: '32px' }}>
                  Agent&apos;inizin temel bilgilerini guncelleyin
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Name */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1a202c',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Agent Adi *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ornek: Code Reviewer Pro"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                      En az 3 karakter olmali
                    </p>
                  </div>

                  {/* Bio */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1a202c',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Bio *
                    </label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Agent'inizin kisa aciklamasi... Ne konularda uzman? Nasil yardimci olabilir?"
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                      En az 20 karakter olmali
                    </p>
                  </div>

                  {/* Avatar URL (optional) */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1a202c',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Avatar URL (Opsiyonel)
                    </label>
                    <input
                      type="text"
                      value={formData.avatar_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, avatar_url: e.target.value }))}
                      placeholder="https://example.com/avatar.png"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                      Bos birakilirsa varsayilan avatar kullanilir
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Persona Selection */}
            {step === 2 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
                  Persona Secimi
                </h2>
                <p style={{ color: '#64748b', marginBottom: '32px' }}>
                  Agent&apos;inizin kisiligini guncelleyin
                </p>
                <PersonaSelector
                  selectedPersonaId={formData.persona_id}
                  onSelect={handlePersonaSelect}
                />
              </div>
            )}

            {/* Step 3: Personality Traits */}
            {step === 3 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
                  Kisilik Ozellikleri
                </h2>
                <p style={{ color: '#64748b', marginBottom: '32px' }}>
                  Agent&apos;inizin kisilik ozelliklerini ayarlayin
                </p>
                <PersonalityTraitsEditor
                  traits={{
                    creativity: formData.personality_creativity,
                    formality: formData.personality_formality,
                    humor: formData.personality_humor,
                    detail: formData.personality_detail,
                    confidence: formData.personality_confidence
                  }}
                  onChange={handleTraitChange}
                />
              </div>
            )}

            {/* Step 4: Expertise Areas */}
            {step === 4 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
                  Uzmanlik Alanlari
                </h2>
                <p style={{ color: '#64748b', marginBottom: '32px' }}>
                  Agent&apos;inizin uzmanlik alanlarini guncelleyin
                </p>
                <ExpertiseSelector
                  selectedExpertise={formData.expertise_areas}
                  onAdd={addExpertise}
                  onRemove={removeExpertise}
                />
              </div>
            )}

            {/* Step 5: Contribution Settings */}
            {step === 5 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
                  Katki Ayarlari
                </h2>
                <p style={{ color: '#64748b', marginBottom: '32px' }}>
                  Agent&apos;inizin katki davranislarini ayarlayin
                </p>
                <ContributionSettings
                  frequency={formData.contribution_frequency}
                  pattern={formData.contribution_pattern}
                  confidenceThreshold={formData.confidence_threshold}
                  onFrequencyChange={(freq) => setFormData(prev => ({ ...prev, contribution_frequency: freq }))}
                  onPatternChange={(pat) => setFormData(prev => ({ ...prev, contribution_pattern: pat }))}
                  onConfidenceThresholdChange={(threshold) => setFormData(prev => ({ ...prev, confidence_threshold: threshold }))}
                />
              </div>
            )}

            {/* Step 6: Preview */}
            {step === 6 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
                  <Sparkles size={24} style={{ display: 'inline', marginRight: '8px' }} />
                  Onizleme
                </h2>
                <p style={{ color: '#64748b', marginBottom: '32px' }}>
                  Agent&apos;inizin nasil davranacagini test edin
                </p>
                <AgentPreview
                  name={formData.name}
                  agentType={formData.agent_type}
                  personality={{
                    creativity: formData.personality_creativity,
                    formality: formData.personality_formality,
                    humor: formData.personality_humor,
                    detail: formData.personality_detail,
                    confidence: formData.personality_confidence
                  }}
                  expertiseAreas={formData.expertise_areas}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '40px',
              paddingTop: '24px',
              borderTop: '1px solid #f1f5f9'
            }}>
              <button
                onClick={step === 1 ? () => navigate(`/agents/${id}`) : handleBack}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
              >
                <ChevronLeft size={16} />
                {step === 1 ? 'Iptal' : 'Geri'}
              </button>

              <button
                onClick={step === 6 ? handleSubmit : handleNext}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Guncelleniyor...' : step === 6 ? 'Degisiklikleri Kaydet' : 'Ileri'}
                {!loading && <ChevronRight size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
